import { request, gql } from 'graphql-request'
import { Presale, Market, ProtocolStats } from '@/types'

interface ContributorData {
  id: string;
  address: string;
  totalContributed: string;
  contributionCount: string;
  firstContributionAt: string;
  lastContributionAt: string;
  presale: {
    id: string;
    question: string;
    state: string;
    raisedAmount: string;
    targetAmount: string;
    deadline: string;
    tokensCreated: boolean;
    lpCreated: boolean;
    resolved: boolean;
    graduatedAt?: string;
    yesOdds: string;
    noOdds: string;
    forceResolvedAnswer?: string;
  };
  contributions: Array<{
    id: string;
    amount: string;
    timestamp: string;
    transactionHash: string;
  }>;
}

const SUBGRAPH_URL = import.meta.env.VITE_SUBGRAPH_URL || ''

// GraphQL queries - Updated to match the actual schema
// Filter out test presales created during deployment
export const GET_PRESALES = gql`
  query GetPresales {
    presales(where: { question_not_in: ["Will Bitcoin reach $100,000 by December 31, 2024?", "KBETS - Esports Betting on Blockchain"] }) {
      id
      question
      targetAmount
      raisedAmount
      creator
      deadline
      contributorCount
      tokensCreated
      lpCreated
      resolved
      state
      yesOdds
      noOdds
      reserveAmount
      lpWithdrawn
      forceResolvedAnswer
      yesToken
      noToken
    }
  }
`

export const GET_PENDING_PRESALES = gql`
  query GetPendingPresales {
    presales(where: { state: "Pending", question_not_in: ["Will Bitcoin reach $100,000 by December 31, 2024?", "KBETS - Esports Betting on Blockchain"] }) {
      id
      question
      targetAmount
      raisedAmount
      creator
      deadline
      contributorCount
      tokensCreated
      lpCreated
      resolved
      state
      yesOdds
      noOdds
      reserveAmount
      lpWithdrawn
      forceResolvedAnswer
      yesToken
      noToken
    }
  }
`

export const GET_GRADUATED_PRESALES = gql`
  query GetGraduatedPresales {
    presales(where: { state: "Graduated", question_not_in: ["Will Bitcoin reach $100,000 by December 31, 2024?", "KBETS - Esports Betting on Blockchain"] }) {
      id
      question
      targetAmount
      raisedAmount
      creator
      deadline
      contributorCount
      tokensCreated
      lpCreated
      resolved
      state
      yesOdds
      noOdds
      reserveAmount
      lpWithdrawn
      resolutionMode
      forceResolvedAnswer
      yesToken
      noToken
    }
  }
`

export const GET_MARKETS = gql`
  query GetMarkets {
    presales(where: { tokensCreated: true, question_not_in: ["Will Bitcoin reach $100,000 by December 31, 2024?", "KBETS - Esports Betting on Blockchain"] }) {
      id
      question
      targetAmount
      raisedAmount
      creator
      deadline
      contributorCount
      tokensCreated
      lpCreated
      resolved
    }
  }
`

export const GET_PROTOCOL_STATS = gql`
  query GetProtocolStats {
    protocol(id: "presale-manager-protocol") {
      totalPresales
      totalVolumeUSD
      totalLiquidityCreated
    }
  }
`

export const GET_USER_CONTRIBUTIONS = gql`
  query GetUserContributions($userAddress: Bytes!) {
    contributors(where: { address: $userAddress }) {
      id
      address
      totalContributed
      contributionCount
      firstContributionAt
      lastContributionAt
      presale {
        id
        question
        state
        raisedAmount
        targetAmount
        deadline
        tokensCreated
        lpCreated
        resolved
        graduatedAt
        yesOdds
        noOdds
        forceResolvedAnswer
      }
      contributions {
        id
        amount
        timestamp
        transactionHash
      }
    }
  }
`

export const GET_USER_CREATED_PRESALES = gql`
  query GetUserCreatedPresales($creatorAddress: Bytes!) {
    presales(where: { creator: $creatorAddress, question_not_in: ["Will Bitcoin reach $100,000 by December 31, 2024?", "KBETS - Esports Betting on Blockchain"] }) {
      id
      question
      targetAmount
      raisedAmount
      creator
      deadline
      contributorCount
      tokensCreated
      lpCreated
      resolved
      state
      createdAt
      forceResolvedAnswer
      yesToken
      noToken
    }
  }
`

// API functions
export async function fetchPresales(): Promise<Presale[]> {
  if (!SUBGRAPH_URL) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_PRESALES) as { presales?: Presale[] }
    return data.presales || []
  } catch (error) {
    console.error('Subgraph error for presales:', error)
    return []
  }
}

export async function fetchPendingPresales(): Promise<Presale[]> {
  if (!SUBGRAPH_URL) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_PENDING_PRESALES) as { presales?: Presale[] }
    return data.presales || []
  } catch (error) {
    console.error('Subgraph error for pending presales:', error)
    return []
  }
}

export async function fetchGraduatedPresales(): Promise<Presale[]> {
  if (!SUBGRAPH_URL) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_GRADUATED_PRESALES) as { presales?: Presale[] }
    return data.presales || []
  } catch (error) {
    console.error('Subgraph error for graduated presales:', error)
    return []
  }
}

export async function fetchMarkets(): Promise<Market[]> {
  if (!SUBGRAPH_URL) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_MARKETS) as { presales?: Market[] }
    return (data.presales || []) as Market[]
  } catch (error) {
    console.error('Subgraph error for markets:', error)
    return []
  }
}

export async function fetchProtocolStats(): Promise<ProtocolStats | null> {
  if (!SUBGRAPH_URL) {
    return null
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_PROTOCOL_STATS) as { protocol?: ProtocolStats }
    return data.protocol || null
  } catch (error) {
    console.error('Subgraph error for protocol stats:', error)
    return null
  }
}

export async function fetchUserContributions(userAddress: string) {
  if (!SUBGRAPH_URL || !userAddress) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_USER_CONTRIBUTIONS, { 
      userAddress: userAddress.toLowerCase() 
    }) as { contributors?: ContributorData[] }
    return data.contributors || []
  } catch (error) {
    console.error('Subgraph error for user contributions:', error)
    return []
  }
}

export async function fetchUserCreatedPresales(creatorAddress: string) {
  if (!SUBGRAPH_URL || !creatorAddress) {
    return []
  }

  try {
    const data = await request(SUBGRAPH_URL, GET_USER_CREATED_PRESALES, { 
      creatorAddress: creatorAddress.toLowerCase() 
    }) as { presales?: Presale[] }
    return data.presales || []
  } catch (error) {
    console.error('Subgraph error for user created presales:', error)
    return []
  }
}

